import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2 } from 'lucide-react';

/**
 * Address autocomplete using Nominatim (OpenStreetMap).
 * As user types, shows matching address suggestions.
 * On select, returns full address + lat/lon.
 */
export default function AddressAutocomplete({ value, onChange, placeholder, className, 'data-testid': testId }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Sync external value
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (text) => {
    if (!text || text.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const encoded = encodeURIComponent(text);
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encoded}&limit=5&addressdetails=1`,
        { headers: { 'User-Agent': 'ThriveBeekeeping/1.0' } }
      );
      const data = await res.json();
      setSuggestions(data.map(r => ({
        display: r.display_name,
        lat: parseFloat(r.lat),
        lon: parseFloat(r.lon),
        suburb: r.address?.suburb || r.address?.town || r.address?.city || '',
        state: r.address?.state || '',
        postcode: r.address?.postcode || '',
      })));
      setOpen(true);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (e) => {
    const text = e.target.value;
    setQuery(text);
    // Notify parent that address text changed (clear coordinates)
    onChange({ address: text, lat: null, lon: null });
    // Debounce search
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(text), 400);
  };

  const handleSelect = (suggestion) => {
    setQuery(suggestion.display);
    setSuggestions([]);
    setOpen(false);
    onChange({
      address: suggestion.display,
      lat: suggestion.lat,
      lon: suggestion.lon,
      suburb: suggestion.suburb,
      state: suggestion.state,
      postcode: suggestion.postcode,
    });
  };

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <div className="relative">
        <Input
          value={query}
          onChange={handleInput}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder || 'Start typing an address...'}
          className={className}
          data-testid={testId}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-stone-400" />}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="address-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => handleSelect(s)}
              className="w-full text-left px-3 py-2.5 hover:bg-amber-50 border-b border-stone-100 last:border-0 transition-colors"
              data-testid={`address-suggestion-${i}`}
            >
              <div className="flex items-start gap-2">
                <MapPin className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-stone-700 leading-tight">{s.display}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
